




import {
  defineComponent,
  onMounted,
  Ref,
  ref,
  watch
} from '@vue/composition-api'
import Mapbox, { GeoJSONSource, Map, Marker, Source } from 'mapbox-gl'
import { TOKEN } from '@/lib/mapbox'
import { Coordinates, Route } from '@/types'
import { useStore } from '@/store'

export default defineComponent({
  name: 'VMap',

  props: {
    route: {
      type: Object as () => Route,
      default: null
    },
    currentLocation: {
      type: Object as () => Coordinates,
      default: () => ({
        lat: 0,
        lon: 0
      })
    }
  },

  setup(props) {
    const { state, updateElapsedRoute } = useStore()
    const mapbox = ref(Mapbox)
    mapbox.value.accessToken = TOKEN

    const mapContainer = ref<HTMLElement>(undefined)
    const map = ref<Map>(undefined)
    const markers = ref<Marker[]>([])
    const startLabel = ref<Marker>(undefined)
    const endLabel = ref<Marker>(undefined)

    const currentLocationMarker: Ref<Marker> = ref(undefined)

    const renderMarkers = () => {
      props.route.steps.forEach((step, index) => {
        // Create marker HTML element
        const markerElement = document.createElement('div')
        markerElement.classList.add(
          'c-marker',
          step.type === 'info'
            ? 'c-marker--info'
            : step.type === 'game'
            ? 'c-marker--game'
            : 'c-marker--warning'
        )
        const iconElement = document.createElement('div')
        iconElement.classList.add(
          'c-marker__icon',
          step.type === 'info'
            ? 'ri-information-line'
            : step.type === 'game'
            ? 'ri-question-line'
            : 'ri-alert-line'
        )
        markerElement.appendChild(iconElement)

        // Add event listener
        markerElement.addEventListener(
          'click',
          e => {
            e.stopPropagation()
            state.popoverStepIndex = index
          },
          false
        )

        // Instanciate marker
        const marker = new mapbox.value.Marker(markerElement)

        // Set marker coordinates
        if (step.poiCoordinates) {
          marker.setLngLat([step.poiCoordinates.lon, step.poiCoordinates.lat])
        } else {
          marker.setLngLat([step.coordinates.lon, step.coordinates.lat])
        }
        if (map.value && step.type !== 'invisible') {
          marker.addTo(map.value)
        }
        markers.value.push(marker)
      })
    }

    const deleteMarkers = () => {
      markers.value.forEach(marker => {
        marker.remove()
      })
      startLabel.value.remove()
      endLabel.value.remove()
      currentLocationMarker.value.remove()
      markers.value = []
    }

    const renderCurrentPosition = () => {
      const markerElement = document.createElement('div')
      markerElement.classList.add('c-location')
      const iconElement = document.createElement('div')
      iconElement.classList.add('c-location__icon', 'ri-riding-line')
      markerElement.appendChild(iconElement)
      if (!currentLocationMarker.value) {
        currentLocationMarker.value = new mapbox.value.Marker(markerElement)
      }
      currentLocationMarker.value.setLngLat([0, 0])
      if (map.value) {
        currentLocationMarker.value.addTo(map.value)

        if (state.debugging) {
          map.value.on('click', function(e) {
            console.log(e.lngLat)
            state.currentLocation = {
              lon: e.lngLat.lng,
              lat: e.lngLat.lat
            }
          })
        }
      }
    }

    const renderRoute = () => {
      if (map.value && props.route) {
        const startElement = document.createElement('div')
        startElement.classList.add('c-route-label')
        startElement.innerHTML = 'start'
        startLabel.value = new mapbox.value.Marker(startElement)
        startLabel.value.setLngLat([
          props.route.geometry.coordinates[0][0],
          props.route.geometry.coordinates[0][1]
        ])
        startLabel.value.addTo(map.value)

        const endElement = document.createElement('div')
        endElement.classList.add('c-route-label')
        endElement.innerHTML = 'einde'
        endLabel.value = new mapbox.value.Marker(endElement)
        endLabel.value.setLngLat([
          props.route.geometry.coordinates[
            props.route.geometry.coordinates.length - 1
          ][0],
          props.route.geometry.coordinates[
            props.route.geometry.coordinates.length - 1
          ][1]
        ])
        endLabel.value.addTo(map.value)

        const routeSource = map.value.getSource('route') as GeoJSONSource
        if (routeSource) {
          routeSource.setData({
            type: 'Feature',
            properties: {},
            geometry: props.route.geometry
          })
        } else {
          map.value.addSource('route', {
            type: 'geojson',
            data: {
              type: 'Feature',
              properties: {},
              geometry: props.route.geometry
            }
          })
        }

        const routeLayer = map.value.getLayer('route')

        if (!routeLayer) {
          map.value.addLayer({
            id: 'route',
            type: 'line',
            source: 'route',
            layout: {
              'line-join': 'round',
              'line-cap': 'round'
            },
            paint: {
              'line-color': '#E20913',
              'line-width': [
                'interpolate',
                ['linear'],
                ['zoom'],
                8,
                (6 / 14) * 8,
                14,
                6,
                18,
                (6 / 14) * 18
              ],
              'line-dasharray': [0, 1.6666],
              'line-opacity': 0.5
            }
          })
        }
      }
    }

    const renderElapsedRoute = () => {
      if (map.value && props.route) {
        const filteredCoordinates = updateElapsedRoute(
          props.currentLocation,
          props.route.geometry.coordinates
        )
        const elapsedRouteSource = map.value.getSource(
          'elapsedRoute'
        ) as GeoJSONSource

        if (elapsedRouteSource) {
          elapsedRouteSource.setData({
            type: 'Feature',
            properties: {},
            geometry: {
              type: 'LineString',
              coordinates: filteredCoordinates
            }
          })
        } else {
          map.value.addSource('elapsedRoute', {
            type: 'geojson',
            data: {
              type: 'Feature',
              properties: {},
              geometry: {
                type: 'LineString',
                coordinates: filteredCoordinates
              }
            }
          })

          const elapsedRouteLayer = map.value.getLayer('elapsedRoute')

          if (!elapsedRouteLayer) {
            map.value.addLayer({
              id: 'elapsedRoute',
              type: 'line',
              source: 'elapsedRoute',
              layout: {
                'line-join': 'round',
                'line-cap': 'round'
              },
              paint: {
                'line-color': '#E20913',
                'line-width': [
                  'interpolate',
                  ['linear'],
                  ['zoom'],
                  8,
                  (6 / 14) * 8,
                  14,
                  6,
                  18,
                  (6 / 14) * 18
                ]
              }
            })
          }
        }
      }
    }

    onMounted(async () => {
      if (mapContainer.value) {
        const center = props.route
          ? props.route.center
          : {
              lat: 51.235735,
              lon: 4.4047893
            }
        // Init map
        map.value = new mapbox.value.Map({
          container: mapContainer.value,
          style: 'mapbox://styles/nbrusthuizen/ckb9aavbg0tbj1iqhzsi7iqqe',
          center: [center.lon, center.lat],
          zoom: 17
        })

        map.value.on('load', function() {
          // Render markers
          renderMarkers()

          // Render route
          renderRoute()
          renderElapsedRoute()

          // Render current position
          renderCurrentPosition()
        })
      }
    })

    const refreshMap = () => {
      // Render markers
      deleteMarkers()
      renderMarkers()

      // Render route
      renderRoute()
      renderElapsedRoute()

      // Render current position
      renderCurrentPosition()

      map.value.jumpTo({
        center: [props.route.center.lon, props.route.center.lat]
      })
    }

    watch(
      () => props.currentLocation,
      newLocation => {
        currentLocationMarker.value &&
          currentLocationMarker.value.setLngLat([
            newLocation.lon,
            newLocation.lat
          ])
        map.value &&
          map.value.flyTo({ center: [newLocation.lon, newLocation.lat] })
        renderElapsedRoute()
      }
    )

    watch(
      () => props.route,
      () => {
        refreshMap()
      },
      {
        lazy: true
      }
    )

    return {
      mapContainer
    }
  }
})
